import React from "react";
import { motion } from "framer-motion";
import { Laptop, PrinterCheck, Megaphone, ListChecks, UserRoundCog, ScissorsLineDashed, Hourglass, ShieldCheck } from "lucide-react";

const timelineData = [
  {
    icon: Laptop,
    year: "2012",
    description: "البداية السحابية وبناء أولى الخدمات الإعلانية الرقمية"
  },
  {
    icon: PrinterCheck,
    year: "2013",
    description: "تأسيس المطبعة كأول توسع فعلي للشركة"
  },
  {
    icon: Megaphone,
    year: "2015",
    description: "إطلاق أول حملة تسويقية متكاملة لعلامات محلية"
  },
  {
    icon: ListChecks,
    year: "2017",
    description: "تأسيس قسم التصميم والهوية البصرية وبناء أول فريق إبداعي"
  },
  {
    icon: UserRoundCog,
    year: "2020",
    description: "تطوير خدمات التصوير والإنتاج الإعلامي"
  },
  {
    icon: ShieldCheck,
    year: "2022",
    description: "تأسيس الإدارة الرئيسية في جدة كمركز للتخطيط والتوسع"
  },
  {
    icon: ScissorsLineDashed,
    year: "2025",
    description: "افتتاح فرع القنفذة كإدارة إقليمية لزان، دعمًا للتوسع وتنفيذ الأعمال الميدانية.    "
  },
  {
    icon: Hourglass,
    year: "2026",
    description: "إطلاق فرع المنطقة الجنوبية – جازان (قريبًا)"
  }
];

const galleryImages = [
  {
    url: "https://imgg.io/images/2025/12/24/b263a6ae93cd8925d4199d1ca9dc69d9.png",
    alt: "نموذج تنفيذ كاب للعلامة التجارية \"ملء\""
  },
  {
    url: "https://imgg.io/images/2025/12/24/5e03331f65d3670d23a9779e57ed0142.png",
    alt: "نموذج من تنفيذ العلامة التجارية \"Tosty\""
  },
  {
    url: "https://imgg.io/images/2025/12/24/1315397c5da013e7e6967b82f4428e07.png",
    alt: "نموذج من تنفيذ هودي للعلامة التجارية \"نفحة بُن\""
  },
  {
    url: "https://imgg.io/images/2025/12/24/87d2562b62dce99c100fecdaffae8604.png",
    alt: "نموذج من تنفيذ الشعار للعلامة التجارية \"ملء\""
  },
  {
    url: "https://imgg.io/images/2025/12/24/72868a78bd0d5c8bf4752eae3d68c3c7.png",
    alt: "نموذج من تنفيذ احد الازياء لافتتاح مركز الأمير محمد بن سلمان"
  },
  {
    url: "https://imgg.io/images/2025/12/24/c1fbec01bf891dc1bf99f5688d2482e4.png",
    alt: "نموذج من تنفيذ احد الازياء لافتتاح مركز الأمير محمد بن سلمان"
  },
  {
    url: "https://imgg.io/images/2025/12/24/b48f65d0a5d6bff3516cf1a59ec9dee9.png",
    alt: "نموذج من تنفيذ زي الطاهي للعلامة التجارية \"الماهر\""
  },
  {
    url: "https://imgg.io/images/2025/12/24/79342c6f96fafef5a6d90ad7a377ce17.png",
    alt: "نموذج من تنفيذ المريله للعلامة التجارية \"دانك\""
  }
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="py-12 relative overflow-hidden border-b-4 border-white bg-[#e8e9eb]
 pt-20 md:pt-24 lg:pt-28 pb-16 md:pb-20 lg:pb-24 bg-gradient-to-b " dir="rtl">
      {/* Background Decorations */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.3 }}
        whileInView={{ opacity: 0.08, scale: 1 }}
        transition={{ duration: 2.5, ease: "easeOut" }}
        viewport={{ once: true }}
        className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#ee9805] rounded-full blur-[120px]"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.3 }}
        whileInView={{ opacity: 0.06, scale: 1 }}
        transition={{ duration: 2.5, ease: "easeOut", delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-20 left-0 w-[500px] h-[500px] bg-[#141d3c] rounded-full blur-[100px]"
      />

      <div className="mx-auto px-6 max-w-7xl lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, type: "spring" }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#141d3c]/10 rounded-full px-5 py-2.5 mb-6"
          >
            <span className="font-cairo text-sm text-[#141d3c] font-bold uppercase tracking-wider">ZAN DIGITAL MARKETING</span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="font-cairo text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
          >
            <span className="text-[#141d3c]">قـصـة زان</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            viewport={{ once: true }}
            className="font-cairo text-xl text-[#ee9805] font-semibold"
          >
            2012 - 2026
          </motion.p>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            viewport={{ once: true }}
            className="font-cairo text-lg md:text-xl text-gray-600 mt-6 max-w-2xl mx-auto leading-relaxed"
          >
            رحلة زان هي رحلة تحول حقيقي … من فكرة إلى كيان، ومن شركة إلى علامة تُلهم.
          </motion.p>
        </motion.div>

        {/* Main Content - Timeline + Images */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-stretch">
          
          {/* Right Side - Timeline Content */}
          <div className="lg:col-span-5 order-1 mt-32">
            <div className="relative">
              {timelineData.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.15 }}
                  viewport={{ once: true }}
                  className="relative mb-8 last:mb-0"
                >
                  <motion.div
                    whileHover={{ scale: 1.02, x: -5 }}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#ee9805]/30 transition-all duration-500 cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <motion.div 
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        className="w-12 h-12 bg-gradient-to-br from-[#141d3c] to-[#141d3c]/80 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:from-[#ee9805] group-hover:to-[#ee9805]/80 transition-all duration-500 shadow-md"
                      >
                        <item.icon className="w-6 h-6 text-white" />
                      </motion.div>
                      
                      {/* Content */}
                      <div className="flex-1 text-right">
                        <h3 className="font-cairo text-2xl font-bold text-[#ee9805] mb-2">
                          {item.year}
                        </h3>
                        <p className="font-cairo text-gray-700 leading-relaxed text-sm">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Center - Vertical Line */}
          <div className="hidden lg:flex lg:col-span-1 justify-center order-2">
            <div className="relative h-full">
              {/* Main Line */}
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: "100%" }}
                transition={{ duration: 3, ease: "easeOut" }}
                viewport={{ once: true }}
                className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#ee9805] via-[#141d3c] via-50% to-[#ee9805] rounded-full"
              />
              
              {/* Decorative Dots - one for each timeline item */}
              {timelineData.map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  viewport={{ once: true }}
                  className="absolute left-1/2 -translate-x-1/2"
                  style={{ top: `${(i / (timelineData.length - 1)) * 100}%` }}
                >
                  <div className={`w-4 h-4 rounded-full ${i % 2 === 0 ? 'bg-[#ee9805]' : 'bg-[#141d3c]'} shadow-lg`}>
                    <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: i % 2 === 0 ? '#ee9805' : '#141d3c' }} />
                  </div>
                </motion.div>
              ))}
              
              {/* Start Marker */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3, type: "spring" }}
                viewport={{ once: true }}
                className="absolute -top-5 left-[calc(50%-2px)] -translate-x-1/2 w-10 h-10 bg-[#ee9805] rounded-full flex items-center justify-center shadow-lg shadow-[#ee9805]/40 z-10"
              >
                <div className="w-4 h-4 bg-white rounded-full" />
              </motion.div>
              
              {/* End Marker */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 2.5, type: "spring" }}
                viewport={{ once: true }}
                className="absolute -bottom-5 left-[calc(50%-2px)] -translate-x-1/2 w-10 h-10 bg-[#141d3c] rounded-full flex items-center justify-center shadow-lg shadow-[#141d3c]/40 z-10"
              >
                <div className="w-4 h-4 bg-white rounded-full" />
              </motion.div>
            </div>
          </div>

          {/* Left Side - Images Gallery */}
          <div className="lg:col-span-6 order-3 mt-20">
            <div className="flex flex-col gap-5">
              {/* Image Row 1 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-2xl shadow-xl h-44"
                
              >
              
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                  src={galleryImages[0].url}
                  alt={galleryImages[0].alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#141d3c]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-3 right-3 bg-[#141d3c] text-white font-cairo text-sm font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                  {galleryImages[0].alt}
                </div>
              </motion.div>

              {/* Image Row 2 - Two images */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.35 }}
                  viewport={{ once: true }}
                  className="relative group overflow-hidden rounded-2xl shadow-xl h-36"
                >
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={galleryImages[1].url}
                    alt={galleryImages[1].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#ee9805]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-2 right-2 bg-[#ee9805] text-white font-cairo text-xs font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                    {galleryImages[1].alt}
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  viewport={{ once: true }}
                  className="relative group overflow-hidden rounded-2xl shadow-xl h-36"
                >
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={galleryImages[2].url}
                    alt={galleryImages[2].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141d3c]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-2 right-2 bg-[#141d3c] text-white font-cairo text-xs font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                    {galleryImages[2].alt}
                  </div>
                </motion.div>
              </div>

              {/* Image Row 3 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.65 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-2xl shadow-xl h-44"
              >
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                  src={galleryImages[3].url}
                  alt={galleryImages[3].alt}
                  className="w-full h-full object-cover object-[center_30%]"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#ee9805]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-3 right-3 bg-[#ee9805] text-white font-cairo text-sm font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                  {galleryImages[3].alt}
                </div>
              </motion.div>

              {/* Image Row 4 - Two images side by side (images 4 & 5) */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  viewport={{ once: true }}
                  className="relative group overflow-hidden rounded-2xl shadow-xl h-36"
                >
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={galleryImages[4].url}
                    alt={galleryImages[4].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141d3c]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-2 right-2 bg-[#141d3c] text-white font-cairo text-xs font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
  {galleryImages[4].alt}
</div>

                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                  viewport={{ once: true }}
                  className="relative group overflow-hidden rounded-2xl shadow-xl h-36"
                >
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    src={galleryImages[5].url}
                    alt={galleryImages[5].alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141d3c]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-2 right-2 bg-[#141d3c] text-white font-cairo text-xs font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                    {galleryImages[5].alt}
                  </div>
                </motion.div>
              </div>

              {/* Image Row 5 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-2xl shadow-xl h-44"
              >
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                  src={galleryImages[6].url}
                  alt={galleryImages[6].alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#141d3c]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-3 right-3 bg-[#141d3c] text-white font-cairo text-sm font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                  {galleryImages[6].alt}
                </div>
              </motion.div>

              {/* Image Row 6 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-2xl shadow-xl h-44"
              >
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                  src={galleryImages[7].url}
                  alt={galleryImages[7].alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-[#ee9805]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-3 right-3 bg-[#ee9805] text-white font-cairo text-sm font-bold px-3 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500">
                  {galleryImages[7].alt}
                </div>
              </motion.div>

              {/* Decorative Element */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r bg-[#141d3c] border-4 border-white rounded-2xl p-5 text-center mt-10 shadow-lg"
              >
                <p className="font-cairo text-white text-lg font-bold">
                  +14 عامًا من الإبداع والتميز
                </p>
                <p className="font-cairo text-white/80 text-sm mt-1">
                  نصنع قصص نجاح لعملائنا
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}